(function () {
  const DEFAULT_THRESHOLD = 5;   // single-size products (overridden by data-threshold on the snippet, if set)
  const COMBO_THRESHOLD = 3;     // co-ords: show message when the selected Top + Bottom has this many or fewer
  const WAIT_FOR_CUSTOMER = false; // true = co-ord message hidden until the customer clicks both rows

  function chipContainer(chip) {
    return chip.closest('fieldset') || chip.closest('[class*="option"]') || chip.parentElement;
  }

  function chipGroups(scope) {
    const chips = Array.from(scope.querySelectorAll('[data-option-value]'));
    const groups = new Map();
    chips.forEach((chip) => {
      const container = chipContainer(chip);
      if (!groups.has(container)) groups.set(container, []);
      groups.get(container).push(chip);
    });
    return Array.from(groups.values());
  }

  function isSelected(chip) {
    return (
      chip.checked === true ||
      chip.getAttribute('aria-checked') === 'true' ||
      chip.getAttribute('aria-selected') === 'true' ||
      chip.dataset.selected === 'true' ||
      chip.classList.contains('is-selected') ||
      chip.classList.contains('selected') ||
      chip.classList.contains('active') ||
      chip.hasAttribute('data-active')
    );
  }

  // The visible size box the badge should sit on (the option element itself may be hidden)
  function badgeTarget(chip) {
    const next = chip.nextElementSibling;
    const el =
      chip.closest('.product__chip') ||
      chip.querySelector('.product__chip') ||
      (next && next.matches('.product__chip, label') ? next : null) ||
      (chip.id && document.querySelector('label[for="' + CSS.escape(chip.id) + '"]')) ||
      chip;
    if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
    return el;
  }

  // "top: m" / "bottom: l" / "size: xs" — read from the row's label
  function groupName(chips) {
    const container = chipContainer(chips[0]);
    const labelEl = container.querySelector('legend, .form__label');
    return (labelEl || container).textContent.trim().toLowerCase();
  }

  const isCoordName = (name) => name.startsWith('top') || name.startsWith('bottom');

  function setup(scope) {
    const dataEl = scope.querySelector('[data-variant-inventory]');
    if (!dataEl) return;

    let variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const threshold = parseInt(dataEl.dataset.threshold, 10);
    const limit = isNaN(threshold) ? DEFAULT_THRESHOLD : threshold;
    if (limit <= 0) return;

    let busy = false;
    const touchedRows = new Set();

    const findVariant = (combo) =>
      variants.find((v) => v.options.every((opt, i) => combo[i] == null || opt === combo[i]));

    // Only touch the DOM when something actually changes (prevents a re-render loop)
    function setBadge(el, text) {
      const existing = el.querySelector('.stock-badge:not(.stock-badge--combo)');
      if (!text) {
        if (existing) existing.remove();
        return;
      }
      if (existing && existing.textContent === text) return;
      if (existing) existing.remove();
      const badge = document.createElement('span');
      badge.className = 'stock-badge';
      badge.textContent = text;
      el.appendChild(badge);
    }

    function comboAnchor(groups, names) {
      const link = Array.from(scope.querySelectorAll('a, button')).find(
        (el) => el.textContent.trim().toLowerCase() === 'size guide'
      );
      if (link) {
        const wrap = link.parentElement;
        return wrap && wrap.children.length <= 3 ? wrap : link;
      }
      for (let i = groups.length - 1; i >= 0; i--) {
        if (isCoordName(names[i])) return chipContainer(groups[i][0]);
      }
      return null;
    }

    function renderComboMessage(text, groups, names) {
      let msg = scope.querySelector('.stock-combo');
      if (!text) {
        if (msg) msg.remove();
        return;
      }
      if (msg && msg.textContent === text) return;
      if (!msg) {
        const anchor = comboAnchor(groups, names);
        if (!anchor) return;
        msg = document.createElement('div');
        msg.className = 'stock-combo';
        anchor.insertAdjacentElement('afterend', msg);
      }
      msg.innerHTML = '';
      const badge = document.createElement('span');
      badge.className = 'stock-badge stock-badge--combo';
      badge.textContent = text;
      msg.appendChild(badge);
    }

    function render() {
      busy = true;

      const groups = chipGroups(scope);
      if (!groups.length) {
        busy = false;
        return;
      }
      const names = groups.map(groupName);
      const current = groups.map((chips) => {
        const sel = chips.find(isSelected);
        return sel ? sel.dataset.optionValue : null;
      });

      if (names.some(isCoordName)) {
        // Co-ords: no badges on the boxes, one message for the selected combination
        groups.forEach((chips) => chips.forEach((chip) => setBadge(badgeTarget(chip), '')));

        let text = '';
        const ready =
          current.every((v) => v != null) && (!WAIT_FOR_CUSTOMER || touchedRows.size >= 2);
        if (ready) {
          const v = findVariant(current);
          if (v && v.tracked && v.available && v.qty > 0 && v.qty <= COMBO_THRESHOLD) {
            text = 'Only ' + v.qty + ' left in this combination';
          }
        }
        renderComboMessage(text, groups, names);
      } else {
        // All other products: badge on each size box, as before
        groups.forEach((chips, gi) => {
          chips.forEach((chip) => {
            const combo = current.slice();
            combo[gi] = chip.dataset.optionValue;
            const v = findVariant(combo);
            const text =
              v && v.tracked && v.available && v.qty > 0 && v.qty <= limit ? v.qty + ' left' : '';
            setBadge(badgeTarget(chip), text);
          });
        });
      }

      requestAnimationFrame(() => {
        busy = false;
      });
    }

    render();

    scope.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-option-value]');
      if (chip) {
        const name = groupName([chip]);
        if (isCoordName(name)) touchedRows.add(name.startsWith('top') ? 'top' : 'bottom');
      }
      setTimeout(render, 50);
    });
    scope.addEventListener('change', () => setTimeout(render, 50));

    new MutationObserver(() => {
      if (busy) return;
      requestAnimationFrame(render);
    }).observe(scope, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-checked', 'aria-selected', 'data-selected'],
    });
  }

  function init() {
    const scopes = document.querySelectorAll('[data-section-type="product"]');
    (scopes.length ? scopes : [document]).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();